import { groupBy } from 'underscore';
import * as Types from './productsApiTypes';

const VALID_STATUS_CODES = [
  // the VariantsController returns Published and Not Published products;
  Types.ProductStatus.NOT_PUBLISHED as string,
  Types.ProductStatus.PUBLISHED as string,
  Types.ProductStatus.FREE as string
];

// Get selectable variant options and return them as a single list
export function getVariantAttributes(selectableAttributes: any[], variant: Types.ApiVariant): Types.Variant {
  // variantOptions are index based. variant.optionCode1 is always the first attribute
  const keys = selectableAttributes.map((obj) => obj.type);

  const mappedAttributes = {
    ...(keys[0] !== undefined && { [keys[0]]: variant.optionCode1 }),
    ...(keys[1] !== undefined && { [keys[1]]: variant.optionCode2 })
  };

  return {
    ...variant,
    variantAttributes: mappedAttributes as Types.VariantAttributes
  };
}

// Map Variant options to usuable options for React
export function variantOptionsToSelectableAttributes(variantOptions: Types.ApiAttribute[]): Types.SelectableVariantAttribute[] {
  const selectableAttributes = variantOptions.map((option) => {
    const key = option.type.code.toLowerCase();
    const values = option.items.map((item) => ({
      code: item.value.code,
      value: item.value.value,
      image: item?.images[0]?.src ?? undefined
    }));

    return {
      type: key,
      code: option.type.code,
      name: option.type.value.toLowerCase(),
      values
    };
  });

  return selectableAttributes;
}

// Attributes to Product Symbols
export function mapProductSymbols(symbols: Types.ApiProductSymbol[]): Types.ProductSymbol[] {
  const mappedSymbols = symbols.map((symbol) => ({
    code: symbol.code.code,
    image: symbol?.images[0]?.src ?? undefined,
    url: symbol.url,
    value: symbol.value.value
  }));

  return mappedSymbols;
}

export function mapUniqueSellingPoints(usp: Types.ApiProductSymbol[]): Types.UniqueSellingPoint[] {
  const mappedUsps = usp.map((usp) => ({
    code: usp.code.code,
    value: usp.value.value
  }));

  return mappedUsps;
}

// Flatten and group attributes to usable 'Technical' data
function mapTechnicalData(attributes: Types.ApiAttribute[]): Types.TechnicalData[] {
  // See sitecore item: `/sitecore/content/Tenants/CarelLurvink/Main/Data/Products/Product Details/Technical/Default`

  const allowedAttributes = ['1000', '1100', '1400', '1500', '1600', '1700', '1655', '1650', '2200', '3200'];

  const technicalData = Object.entries(attributes)
    .flatMap(([code, attributeList]) => {
      if (allowedAttributes.indexOf(code) === -1) {
        return undefined;
      }

      return attributeList.items.map((listItem) => {
        if (listItem.value.displayOrder < 0) {
          return undefined;
        }

        return {
          code: listItem.code.code,
          image: listItem.images[0]?.src,
          name: listItem.code.value,
          value: listItem.value.value
        };
      });
    })
    .filter(Boolean);

  const groupedData = groupBy(technicalData, 'code');
  const mergedData = Object.entries(groupedData).map(([key, dataValues]) => {
    const name = dataValues[0]?.name ?? '';
    // @ts-expect-error: TS does not infere `.filter(Boolean)`
    const values = dataValues.reduce<string[]>((acc, curr) => [...acc, curr.value], []);
    return {
      code: key,
      name,
      value: values.join(', ')
    };
  });

  return mergedData;
}

// Map product data to usable data
export function mapCatalogProduct(product: Types.ApiProduct): Types.Product {
  const selectableVariantAttributes = variantOptionsToSelectableAttributes(product.variantOptions);
  const variants = product.variants.map((variant) => getVariantAttributes(selectableVariantAttributes, variant));

  const { variantOptions, symbols = [], ...newProduct } = product;
  const mappedSymbols = mapProductSymbols(symbols);
  // @ts-expect-error: TS and optional chaining / nullish coalescing :(
  const uniqueSellingPoints = mapUniqueSellingPoints(product?.attributes['PDPLUS']?.items ?? []);
  // @ts-expect-error: TS and optional chaining / nullish coalescing :(
  const technicalData = mapTechnicalData(product?.attributes ?? []);

  const mappedProduct = {
    ...newProduct,
    variants: variants ?? [],
    selectableVariantAttributes,
    ...(mappedSymbols && { symbols: mappedSymbols }),
    uniqueSellingPoints,
    technicalData
  };

  return mappedProduct;
}

// Remove products that can't be displayed
export function filterInValidProducts(product: Types.ApiProduct): boolean {
  if (!VALID_STATUS_CODES.includes(product.status)) {
    return false;
  }
  if (!product.variants || product.variants?.length === 0) {
    return false;
  }
  if (!product.visible) {
    return false;
  }

  return true;
}

export function transformGetProductsById(rawResponse: Types.ApiProduct[]): Types.Product[] | undefined {
  // return rawResponse.map((product) => mapCatalogProduct(product));
  const products = rawResponse?.filter(filterInValidProducts).map((product) => mapCatalogProduct(product));
  return products?.length > 0 ? products : undefined;
}
